/**
 * useWakeLock Hook
 * Hybrid Wake Lock implementation met native API + NoSleep.js fallback
 */
import { useEffect, useRef, useState } from 'react';
import NoSleep from 'nosleep.js';

interface WakeLockState {
  isActive: boolean;
  isSupported: boolean;
  method: 'native' | 'nosleep' | null;
}

export const useWakeLock = () => {
  const [state, setState] = useState<WakeLockState>({
    isActive: false,
    isSupported: false,
    method: null,
  });

  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const noSleepRef = useRef<NoSleep | null>(null);

  useEffect(() => {
    // Check support
    const nativeSupported = 'wakeLock' in navigator;
    setState(prev => ({
      ...prev,
      isSupported: true, // NoSleep fallback altijd beschikbaar
    }));

    console.log('Wake Lock support:', {
      native: nativeSupported,
      fallback: true,
    });

    // Initialize NoSleep instance
    noSleepRef.current = new NoSleep();

    // Cleanup on unmount
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
      }
      if (noSleepRef.current) {
        noSleepRef.current.disable();
      }
    };
  }, []);

  const requestWakeLock = async (): Promise<boolean> => {
    console.log('🔆 Requesting wake lock...');

    try {
      // Try native Wake Lock API first
      if ('wakeLock' in navigator) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          
          console.log('✅ Native Wake Lock acquired');
          
          setState({
            isActive: true,
            isSupported: true,
            method: 'native',
          });

          // Handle wake lock release (e.g., tab becomes inactive)
          wakeLockRef.current.addEventListener('release', () => {
            console.log('Wake Lock released');
            setState(prev => ({ ...prev, isActive: false, method: null }));
          });

          return true;
        } catch (nativeError: any) {
          console.warn('Native Wake Lock failed, trying fallback:', nativeError.message);
          // Fall through to NoSleep fallback
        }
      }

      // Fallback to NoSleep.js
      if (noSleepRef.current) {
        noSleepRef.current.enable();
        console.log('✅ NoSleep.js fallback activated');
        
        setState({
          isActive: true,
          isSupported: true,
          method: 'nosleep',
        });

        return true;
      }

      return false;
    } catch (error: any) {
      console.error('❌ Wake Lock error:', error);
      setState({
        isActive: false,
        isSupported: true,
        method: null,
      });
      return false;
    }
  };

  const releaseWakeLock = async (): Promise<void> => {
    console.log('🌙 Releasing wake lock...');

    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        console.log('✓ Native Wake Lock released');
      }

      if (noSleepRef.current) {
        noSleepRef.current.disable();
        console.log('✓ NoSleep.js disabled');
      }

      setState({
        isActive: false,
        isSupported: true,
        method: null,
      });
    } catch (error: any) {
      console.error('Error releasing wake lock:', error);
    }
  };

  return {
    ...state,
    requestWakeLock,
    releaseWakeLock,
  };
};